/**
 * Pure sentence-builder for the READS AS band (EAINT-877 Phase 4) — assembles
 * bands 01-03 (relation, tree, pins) into one plain-language sentence. Never
 * touches membership counts; those come from the server (see ReadsAsBand).
 */
import { readsAsPredicate, type ReadsAsContext } from '../../../utils/cfGroup';
import type { RollupSetModel } from './rollupSetModel';

/** The `t(key, defaultValue, options)` shape every band in this card already calls — `defaultValue`
 * is required (not optional) so this stays structurally assignable from `TFunction`'s own
 * `(key, defaultValue: string, options?) => string` overload; every call site in this card already
 * supplies one. */
export type ReadsAsTranslateFn = (key: string, defaultValue: string, options?: Record<string, unknown>) => string;

/** The roster's exclusions as READS AS names them: only what the caller may see, plus the engine's total. */
export interface ReadsAsExclusions {
  names: string[];
  total: number;
}

const MAX_NAMED_EXCLUSIONS = 2;

export interface ReadsAsSentenceParams {
  set: RollupSetModel;
  /** The roster's exclusions; when absent (no roster mounted) the decompiled count is quoted instead. */
  excluded?: ReadsAsExclusions;
  t: ReadsAsTranslateFn;
  // The source fields are `T | undefined`; use NonNullable so `?` alone marks optionality
  // (avoids the redundant `T | undefined | undefined` SonarCloud flagged).
  availableProperties?: NonNullable<ReadsAsContext['availableProperties']>;
  availableRelationships?: NonNullable<ReadsAsContext['availableRelationships']>;
  availablePerspectives?: NonNullable<ReadsAsContext['availablePerspectives']>;
  locale?: string;
}

function relationPhrase(set: RollupSetModel, t: ReadsAsTranslateFn): string {
  if (set.scope === 'any') {
    return t('statusFactors.set.readsAs.relation.any', 'Any sphere in the organisation');
  }
  return set.direction === 'inbound'
    ? t('statusFactors.set.readsAs.relation.inbound', 'Spheres that reach this one')
    : t('statusFactors.set.readsAs.relation.outbound', 'Spheres this one reaches');
}

function excludedNamesClause(excluded: ReadsAsExclusions, t: ReadsAsTranslateFn, locale?: string): string {
  if (excluded.total === 0) return '';
  const shown = excluded.names.slice(0, MAX_NAMED_EXCLUSIONS);
  if (shown.length === 0) {
    return t('statusFactors.set.readsAs.excludedUnseen', ", never {{count}} you can't see", {
      count: excluded.total,
    });
  }
  const others = excluded.total - shown.length;
  if (others === 0) {
    const names = new Intl.ListFormat(locale, { type: 'disjunction' }).format(shown);
    return t('statusFactors.set.readsAs.excludedNamed', ', never {{names}}', { names });
  }
  const names = new Intl.ListFormat(locale, { type: 'unit', style: 'long' }).format(shown);
  return others === 1
    ? t('statusFactors.set.readsAs.excludedNamedOne', ', never {{names}} and {{count}} other', { names, count: others })
    : t('statusFactors.set.readsAs.excludedNamedMany', ', never {{names}} and {{count}} others', {
        names,
        count: others,
      });
}

/**
 * Assembles `<relation phrase> where <conditions>, plus N always included, never M excluded by
 * hand.` — every clause whose part is empty is omitted entirely (never "where nothing").
 */
export function buildReadsAsSentence(params: ReadsAsSentenceParams): string {
  const { set, t, excluded, availableProperties, availableRelationships, availablePerspectives, locale } = params;

  const predicate = readsAsPredicate(set.tree, {
    // The real `t` from `useTranslation()` satisfies this narrower shape at runtime — every
    // caller of `readsAsPredicate` (UnifiedFilterTreeMolecule included) hands it the same hook.
    t: t as unknown as ReadsAsContext['t'],
    availableProperties,
    availableRelationships,
    availablePerspectives,
    locale,
  });

  let sentence = relationPhrase(set, t);

  if (predicate) {
    sentence += ` ${t('statusFactors.set.readsAs.whereClause', 'where {{predicate}}', { predicate })}`;
  }

  if (set.pins.length > 0) {
    sentence += t('statusFactors.set.readsAs.pinsClause', ', plus {{count}} always included', {
      count: set.pins.length,
    });
  }

  if (excluded) {
    sentence += excludedNamesClause(excluded, t, locale);
  } else if (set.authorExcluded.length > 0) {
    sentence += t('statusFactors.set.readsAs.excludedClause', ', never {{count}} excluded by hand', {
      count: set.authorExcluded.length,
    });
  }

  return `${sentence}.`;
}
